/* eslint-disable no-restricted-globals */
import _ from 'underscore';
import $ from 'jquery';
import { merge } from '@bingads-webui-universal/primitive-utilities';
import * as urlUtil from '@bingads-webui/url-util';
import { CAMPAIGN_TYPES, PREFERENCES_ACTION_TYPES } from '@bingads-webui-campaign/recommendation-preferences';
import { fromLegacyTimeZone, toIANATimeZone } from '@bingads-webui-universal/time-zone-converter';
import { editorialErrorCodeToErrorMessageMapping } from '@bingads-webui-campaign/api-errors';

import {
  RECOMMENDATION_TYPES,
  CHANNEL_ALL_RECOMMENDATIONS,
  RECOMMENDATION_IDS,
  CHANNEL_TYPES,
  SUMMARY_LEVELS,
  ESTIMATE_TYPES,
  ADINSIGHT_LOG_API_NAME,
  ADINSIGHT_LOG_ACTION_TYPE,
  RECOMMENDATION_DISMISS_FEEDBACK_TYPE,
  GA_NO_PILOTING_TYPES,
  MCC_NOT_SUPPORTED_TYPE,
  RECOMMENDATION_IMAGE_EXTENSION_PAGE_SIZES,
  RECOMMENDATION_IMAGE_EXTENSION_PAGE_SIZES_TEN,
  RECOMMENDATION_IMAGE_EXTENSION_PAGE_SIZES_TWENTY,
} from './consts';

import {
  getRecommendationPreferences,
  saveRecommendationPreferences,
} from './general-preferences';

import { recommendationNewTextConfigs } from './recommendation-configs';
/**
 * Check if a recommendation is for competition tab or recommendation tab
 * @param {string} type - recommendation type
 * @return {boolean} true for competition tab, false for recommendation tab
 */
export const isCompetition = (type) => {
  const competitionTypes = CHANNEL_ALL_RECOMMENDATIONS[CHANNEL_TYPES.COMPETITION];
  return _.contains(competitionTypes, type);
};

/**
 * Check recommendaton type enabling by parameter
 * @param {string} type - recommendation type
 * @return {boolean} true for enabled, false for disabled
 */
const isEnabledByUrlParameter = (type) => {
  const enabledRecommendationType = urlUtil.getParameter(`IsRecommendationType${type}Enabled`);
  return enabledRecommendationType === 'true';
};

/**
 * Is a recommendation type enabled based permission and channel.
 * @param {string} type - recommendation type
 * @param {string} channel - channel for competition or recommendation
 * @param {Object} permissions - permission object
 * @param {Object} isMCC - is MCC or not
 * @return {boolean} true for enabled, false for disabled
 */
export const isRecommendationTypeEnabled = (
  type,
  channel,
  permissions,
  isMCC = false
) => {
  const supportedTypes = CHANNEL_ALL_RECOMMENDATIONS[channel];
  if (supportedTypes && supportedTypes.indexOf(type) !== -1) {
    if (isEnabledByUrlParameter(type)) {
      return true;
    }

    const isFromMCC = isMCC || channel === CHANNEL_TYPES.MCCRECOMMENDATION || channel === CHANNEL_TYPES.MCCOVERVIEW;

    if (isFromMCC && _.contains(MCC_NOT_SUPPORTED_TYPE, type)) {
      return false;
    }

    if (_.contains(GA_NO_PILOTING_TYPES, type)) {
      return true;
    }

    switch (type) {
      case RECOMMENDATION_TYPES.BUDGET:
        return permissions.IsRecommendationTypeBudgetEnabled;
      // [Recommendation Deprecation] Softly remove CompetitiveBidOpportunity from UI
      // TODO: Clean up all related code regarding CompetitiveBidOpportunity
      case RECOMMENDATION_TYPES.COMPETITIVE_BID:
        return false;
      // [Recommendation Deprecation] Softly remove DeviceBidBoost from UI
      // TODO: Clean up all related code regarding DeviceBidBoost
      case RECOMMENDATION_TYPES.DEVICE_BID_BOOST:
        return false;
      // [Recommendation Deprecation] Softly remove LocationBidBoost from UI
      // TODO: Clean up all related code regarding LocationBidBoost
      case RECOMMENDATION_TYPES.LOCATION_BID_BOOST:
        return false;
      case RECOMMENDATION_TYPES.NEW_LOCATION:
        return false;
      case RECOMMENDATION_TYPES.SYNDICATION:
        return false;
      case RECOMMENDATION_TYPES.IMAGE_EXTENSION:
        return permissions.IsRecommendationTypeImageExtensionEnabled;
      case RECOMMENDATION_TYPES.REPAIR_MISSING_KEYWORD_PARAMS:
        return permissions.IsRecommendationTypeRepairMissingKeywordParamsEnabled;
      case RECOMMENDATION_TYPES.FIX_NO_IMPRESSION_BID:
        return permissions.IsFixNoImpressionBidRecommendationEnabled;
      case RECOMMENDATION_TYPES.IMPROVE_RESPONSIVE_SEARCH_ADS:
        return permissions.IsRecommendationTypeImproveResponsiveSearchAdsEnabled;
      case RECOMMENDATION_TYPES.SSC_2_PMAX_MIGRATION:
        return false;
      case RECOMMENDATION_TYPES.PMAX_IMPORT:
        return permissions.IsRecommendationTypePmaxImportEnabled;
      case RECOMMENDATION_TYPES.DSA_2_PMAX_MIGRATION:
        return permissions.IsRecommendationTypeDSA2PMaxMigrationEnabled;
      case RECOMMENDATION_TYPES.SYNDICATION_GAP:
        return permissions.IsRecommendationTypeSyndicationGapEnabled;
      case RECOMMENDATION_TYPES.AUDIENCE_VIDEOASSET:
        return permissions.IsVideoRecommendationForAudienceCampaignsEnabled;
      default:
        return false;
    }
  }

  return false;
};

/**
 * Get enabled recommendation types.
 * If recommendation type is given, return its Id (if enabled)
 * If recommendation type is not given, return all enabled type for this channel
 * @param {string} recommendationType - recommendation type
 * @param {string} channel - channel for competition or recommendation
 * @param {Object} permissions - permission object
 * @param {Object} isMCC - is MCC or not
 * @return {Array} enabled type collection
 */
export const getRecommendationTypes = (
  recommendationType,
  channel,
  permissions,
  isMCC = false
) => {
  const types = recommendationType ? [recommendationType] : CHANNEL_ALL_RECOMMENDATIONS[channel];
  const enabledTypes = _.filter(
    types,
    type => isRecommendationTypeEnabled(type, channel, permissions, isMCC)
  );

  return enabledTypes;
};

/**
 * Get enabled recommendation ids.
 * If recommendation type is given, return its Id (if enabled)
 * If recommendation type is not given, return all enabled Ids for this channel
 * @param {string} recommendationType - recommendation type
 * @param {string} channel - channel for competition or recommendation
 * @param {Object} permissions - permission object
 * @param {Object} isMCC - is MCC or not
 * @return {Array} enabled id collection
 */
export const getRecommendationIds = (
  recommendationType,
  channel,
  permissions,
  isMCC = false
) => {
  const types = recommendationType ?
    _.flatten([recommendationType]) : CHANNEL_ALL_RECOMMENDATIONS[channel];
  const enabledTypes = _.filter(
    types,
    type => isRecommendationTypeEnabled(type, channel, permissions, isMCC)
  );

  return _.map(enabledTypes, type => RECOMMENDATION_IDS[type]);
};

/**
 * Get preference data
 * @param {Object} preferencesService - preference service
 * @param {string} name - data name
 * @return {Object} preference data
 */
export function getPreferences(preferencesService, name) {
  const sessionData = preferencesService.findByNameAtUserLevel(name);

  return _.result(sessionData, 'Data', {});
}

/**
 * Save preference data
 * @param {Object} preferencesService - preference service
 * @param {string} name - data name
 * @param {Object} data - preference data to be saved
 * @return {undefined}
 */
export function savePreferences(preferencesService, name, data) {
  const Data = merge(
    {},
    getPreferences(preferencesService, name),
    data
  );

  preferencesService.setAtUserLevel({
    Name: name,
    Data,
  });
}

/**
 * Check if it is read only user or account is inactive
 * @param {Object} appConfig - legacy module 'component/config/index'
 * @param {bool} isMCC - is or not customer level
 * @return {bool} true if edit is not allowed
 */
export function isReadOnly(appConfig, isMCC = false) {
  return !!(appConfig.get('Permissions').IsReadOnlyUser || (!isMCC && appConfig.get('CurrentAccountInActive')));
}

const levelWeight = {
  [SUMMARY_LEVELS.CUSTOMER]: 4,
  [SUMMARY_LEVELS.ACCOUNT]: 3,
  [SUMMARY_LEVELS.CAMPAIGN]: 2,
  [SUMMARY_LEVELS.ADGROUP]: 1,
  [SUMMARY_LEVELS.KEYWORD]: 0,
};

/**
 * Check if a aggregate level is a parent of the scope.
 * E.g. Campaign level is a parent of Ad Group scope
 * @param {string} scope - the scope (e.g. Campaign, Ad Group)
 * @param {string} level - the level (e.g. Campaign, Ad Group)
 * @return {bool} true if level is a parent of the scope.
 */
export function isParentLevel(scope, level) {
  return levelWeight[scope] < levelWeight[level];
}

export function getIncreaseSymbol(value, type = 'normal') {
  const map = {
    normal: '+',
    arrow: '↑ ',
  };
  return value >= 0 ? map[type] : '';
}

/**
 * Check if an estimate is positive.
 * E.g. increase of cost should not be highlighted
 * @param {number} value - the delta value
 * @param {string} type - type of estimates
 * @return {bool} true if it should be highlighted
 */
export function isPositive(value, type = 'default') {
  return type === ESTIMATE_TYPES.COST ? value <= 0 : value > 0;
}

/**
 *
 *
 * @param {object} $el - element
 * @param {number} extra - extra bottom need to show
 * @returns {boolean} if bottom of the element in screen
 */
export function checkBottomInScreen($el, extra = 0) {
  return ($el.offset().top + $el.outerHeight() > $(document).scrollTop()) &&
    ($el.offset().top + $el.outerHeight() + extra < $(window).height() + $(document).scrollTop());
}

/**
 *
 *
 * @param {object} $el - element
 * @param {number} extra - extra bottom need to show
 * @returns {boolean} if the element will extend the height of document return false else true
 */
export function CheckDocBottomMargin($el, extra = 0) {
  return $el.offset().top + $el.outerHeight() + extra < $(document).height();
}

export function dismissFeedback(op) {
  const {
    campaignId,
    currentActivity,
    feedback: { preference, reason },
    preferencesService,
    rejectAutoApplySuggestedAds,
    context,
    guid,
    isMCC,
  } = op;
  const { type, expireTime } = preference;
  let api = ADINSIGHT_LOG_API_NAME.RECOMMENDATION;
  let channel = isMCC ? CHANNEL_TYPES.MCCRECOMMENDATION : CHANNEL_TYPES.RECOMMENDATION;

  if (isCompetition(type)) {
    api = ADINSIGHT_LOG_API_NAME.COMPETITION;
    channel = CHANNEL_TYPES.COMPETITION;
  }
  currentActivity.trace({
    type: type || '',
    typeId: RECOMMENDATION_IDS[type],
    action: ADINSIGHT_LOG_ACTION_TYPE.FEEDBACK,
    input: reason,
    campaignId,
    channel,
    context: JSON.stringify(context),
    guid,
  }, api);
  const name = rejectAutoApplySuggestedAds ?
    CAMPAIGN_TYPES.RejectAutoApplySuggestedAds :
    CAMPAIGN_TYPES.DismissFeedback;

  saveRecommendationPreferences(
    {
      [name]: { [type]: expireTime },
    },
    preferencesService
  );
}

export function hideInContextRecommendation(op) {
  const {
    levelAt,
    id,
    type,
    currentActivity,
    preferencesService,
    channel,
    campaignId,
    context,
    isCardView,
    guid,
  } = op;

  currentActivity.trace({
    type,
    typeId: RECOMMENDATION_IDS[type],
    action: ADINSIGHT_LOG_ACTION_TYPE.HIDE,
    channel,
    campaignId,
    context: JSON.stringify(context),
    guid,
  }, ADINSIGHT_LOG_API_NAME.RECOMMENDATION);
  const name = isCardView ?
    PREFERENCES_ACTION_TYPES.HidedRecommendationCards :
    PREFERENCES_ACTION_TYPES.HidedRecommendationBanner;
  const preferences = getRecommendationPreferences(preferencesService);
  const levelPreferences = _.chain(preferences)
    .result(`${name}`)
    .result(`${type}`)
    .result(`${levelAt}`, [])
    .value();

  levelPreferences.push(id);

  saveRecommendationPreferences(
    {
      [name]: {
        [type]: {
          [levelAt]: levelPreferences,
        },
      },
    },
    preferencesService
  );
}

export function isShowAutoApplyFeedback(op) {
  const { preferencesService, type, rejectAutoApplySuggestedAds } = op;
  const data = getRecommendationPreferences(preferencesService);
  const preference = data[rejectAutoApplySuggestedAds ?
    CAMPAIGN_TYPES.RejectAutoApplySuggestedAds :
    CAMPAIGN_TYPES.DismissFeedback];
  if (!RECOMMENDATION_DISMISS_FEEDBACK_TYPE[type]) {
    return false;
  }
  if (preference && preference[type]) {
    return Date.now() > preference[type];
  }
  return true;
}

export function getChannel(
  type,
  isInContext,
  isMCC = false,
  isOverviewPage = false,
  inContextCard = false
) {
  if (isCompetition(type)) {
    return CHANNEL_TYPES.COMPETITION;
  } else if (isInContext) {
    return CHANNEL_TYPES.INCONTEXT;
  } else if (isMCC && isOverviewPage) {
    return CHANNEL_TYPES.MCCOVERVIEW;
  } else if (isMCC) {
    return CHANNEL_TYPES.MCCRECOMMENDATION;
  } else if (inContextCard) {
    return CHANNEL_TYPES.INCONTEXTCARD;
  }
  return CHANNEL_TYPES.RECOMMENDATION;
}

/**
 * group UserInputs to {AccountId: [Userinputs]} for bulk apply
 *
 * @param {object} options - options
 * @param {array} options.userInputs - ungrouped userInputs
 * @param {array} options.opportunities - opportunities,
 * the options.userInputs must generate from users.opportunities
 * @returns {object} grouped userinputs
 */
export function groupUserInputs({
  userInputs,
  opportunities,
}) {
  const groupedInputs = {};

  _.map(userInputs, (userInput) => {
    const opportunity = _.find(
      opportunities,
      oppo => oppo.OpportunityId === userInput.OpportunityId
    );
    const accountId = opportunity.Target.AccountId;
    if (!_.isUndefined(groupedInputs[accountId])) {
      groupedInputs[accountId].push(userInput);
    } else {
      groupedInputs[accountId] = [userInput];
    }
  });

  return _.map(groupedInputs, (val, key) => ({
    Key: parseInt(key, 10),
    Value: val,
  }));
}

/**
 * format MT date to human readable string with time zone
 *
 * @param {int} startDate - startDate in int style
 * @param {object} i18n - i18n
 * @param {int} timeZone - time zone in int
 * @returns {string} human readable date string
 */
export function formatStartDate(startDate, i18n, timeZone) {
  const applyDateStr = startDate.toString();
  const applyDate = i18n.parseDate(`${applyDateStr.substring(0, 4)}/${applyDateStr.substring(4)}`, { raw: 'yyyy/MMddHHmmss', timeZone: 'UTC' });
  const accountTimeZone = toIANATimeZone(fromLegacyTimeZone(timeZone));
  return i18n.formatDate(_.max([applyDate, new Date()]), { timeZone: accountTimeZone });
}

export function getIconClasses(statusId, count) {
  const classes = {};
  let cur = 0;

  while (cur < count) {
    if (cur === statusId) {
      classes[`icon${cur}`] = 'status-current';
    } else if (cur < statusId) {
      classes[`icon${cur}`] = 'status-complete';
    } else {
      classes[`icon${cur}`] = 'status-next';
    }
    cur += 1;
  }
  return classes;
}

export function getNewTextConfigByPermission(recommendationType, permissions = {}, configType) {
  const config = recommendationNewTextConfigs[recommendationType];
  let newConfig;

  if (config && permissions[config.permission]) {
    newConfig = config[configType];
  }
  return newConfig;
}

export function getShiftedDate(shift) {
  const now = new Date();
  const shiftedDate = new Date(now.setDate(now.getDate() + (shift || 0)));

  return shiftedDate.toISOString().slice(0, 10);
}

export const getDestUrlData = (
  destUrlCurrentSetting,
  destUrlSuggestedSetting,
  goalOperatorTypes,
  i18n
) => ({
  currentDestinationURLOperatorName:
      goalOperatorTypes[destUrlCurrentSetting.DestinationURLOperatorCode] ?
        i18n.getString(goalOperatorTypes[destUrlCurrentSetting.DestinationURLOperatorCode])
        : destUrlCurrentSetting.DestinationURLOperatorName,
  currentDestinationUrl: destUrlCurrentSetting.DestinationUrl,
  suggestedDestinationURLOperatorName:
      goalOperatorTypes[destUrlSuggestedSetting.DestinationURLOperatorCode] ?
        i18n.getString(goalOperatorTypes[destUrlSuggestedSetting.DestinationURLOperatorCode])
        : destUrlSuggestedSetting.DestinationURLOperatorName,
  suggestedDestinationUrl: destUrlSuggestedSetting.DestinationUrl,
});

export const getEventData = (
  eventCurrentSetting,
  eventSuggestedSetting,
  goalOperatorTypes,
  i18n
) => ({
  categoryTitle: i18n.getString('UnifiedEventTracking_Goal_Creation_Type_Event_Category'),
  currentCategoryName: eventCurrentSetting.Category,
  currentCategoryOperatorName:
    goalOperatorTypes[eventCurrentSetting.CategoryOperatorCode] ?
      i18n.getString(goalOperatorTypes[eventCurrentSetting.CategoryOperatorCode])
      : eventCurrentSetting.CategoryOperatorName,
  suggestedCategoryName: eventSuggestedSetting.Category,
  suggestedCategoryOperatorName:
    goalOperatorTypes[eventSuggestedSetting.CategoryOperatorCode] ?
      i18n.getString(goalOperatorTypes[eventSuggestedSetting.CategoryOperatorCode])
      : eventSuggestedSetting.CategoryOperatorName,
  actionTitle: i18n.getString('UnifiedEventTracking_Goal_Creation_Type_Event_Action'),
  currentActionName: eventCurrentSetting.Action,
  currentActionOperatorName:
    goalOperatorTypes[eventCurrentSetting.ActionOperatorCode] ?
      i18n.getString(goalOperatorTypes[eventCurrentSetting.ActionOperatorCode])
      : eventCurrentSetting.ActionOperatorName,
  suggestedActionName: eventSuggestedSetting.Action,
  suggesteActionOperatorName:
    goalOperatorTypes[eventSuggestedSetting.ActionOperatorCode] ?
      i18n.getString(goalOperatorTypes[eventSuggestedSetting.ActionOperatorCode])
      : eventSuggestedSetting.CategoryOperatorName,
  labelTitle: i18n.getString('UnifiedEventTracking_Goal_Creation_Type_Event_Label'),
  currentLabelName: eventCurrentSetting.Label,
  currentLabelOperatorName:
    goalOperatorTypes[eventCurrentSetting.LabelOperatorCode] ?
      i18n.getString(goalOperatorTypes[eventCurrentSetting.LabelOperatorCode])
      : eventCurrentSetting.LabelOperatorName,
  suggestedLabelName: eventSuggestedSetting.Label,
  suggestedLabelOperatorName:
    goalOperatorTypes[eventSuggestedSetting.LabelOperatorCode] ?
      i18n.getString(goalOperatorTypes[eventSuggestedSetting.LabelOperatorCode])
      : eventSuggestedSetting.LabelOperatorName,
  valueTitle: i18n.getString('UnifiedEventTracking_Goal_Creation_Type_Event_Value'),
  currentValueName: eventCurrentSetting.Value,
  currentValueOperatorName:
    goalOperatorTypes[eventCurrentSetting.ValueOperatorCode] ?
      i18n.getString(goalOperatorTypes[eventCurrentSetting.ValueOperatorCode])
      : eventCurrentSetting.ValueOperatorName,
  suggestedValueName: eventSuggestedSetting.Value,
  suggesteValueOperatorName:
    goalOperatorTypes[eventSuggestedSetting.ValueOperatorCode] ?
      i18n.getString(goalOperatorTypes[eventSuggestedSetting.ValueOperatorCode])
      : eventSuggestedSetting.ValueOperatorName,
});

export function isPlural(value) {
  return value > 1 ? 'Plural' : 'Singular';
}

export const getEditorialErrorMessage = (policyCode) => {
  const policyErrorStr = `Error_Editorial_Raw_${policyCode}`;
  let editorialErrorMessage = editorialErrorCodeToErrorMessageMapping[policyErrorStr];
  if (_.isUndefined(editorialErrorMessage)) {
    editorialErrorMessage = editorialErrorCodeToErrorMessageMapping.EditorialReasons_Default;
  }
  return editorialErrorMessage;
};

export const isDFMEnabled = () => {
  const previousRoute = _.property(['state', 'state', 'previousRoute'])(history);
  return _.isUndefined(previousRoute)
    || (previousRoute && !previousRoute.includes('/recommendations/'));
};

export const getImageExtensionPageSize = (permissions) => {
  let pageSize = RECOMMENDATION_IMAGE_EXTENSION_PAGE_SIZES.slice();
  if (permissions.IsRecommendationIncreaseImageExtensionPageSizePhase2Enabled) {
    pageSize = RECOMMENDATION_IMAGE_EXTENSION_PAGE_SIZES_TWENTY.slice();
  } else if (permissions.IsRecommendationIncreaseImageExtensionPageSizePhase1Enabled) {
    pageSize = RECOMMENDATION_IMAGE_EXTENSION_PAGE_SIZES_TEN.slice();
  }
  return pageSize;
};

export const getFieldValueFromGridData = (gridData, field) => {
  let value;
  const campaignGridData = _.first(_.where(gridData, { Type: 'Campaign' }));
  const adGroupProductPartitionGridData = _.first(_.where(gridData, { Type: 'Ad Group Product Partition' }));
  if (!_.isUndefined(campaignGridData)) {
    if (field === 'CampaignName') {
      value = campaignGridData.CampaignName;
    } else if (field === 'AdGroupName') {
      value = adGroupProductPartitionGridData.AdGroupName;
    } else if (field === 'ProductCondition1') {
      value = adGroupProductPartitionGridData.ProductCondition1;
    } else if (field === 'Bid') {
      value = adGroupProductPartitionGridData.Bid;
    } else if (field === 'Budget') {
      value = campaignGridData.Budget;
    } else if (field === 'Priority') {
      value = campaignGridData.Priority;
    } else if (field === 'StoreId') {
      value = campaignGridData.StoreId;
    }
  }
  return value;
};
